import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  recordActionClick,
  updateActionClickSentiment,
  updateActionClickStatus,
  type ActionSummaryData,
  type EnrichedAction,
  type SignalType,
  type ActionTypeEnum,
  type SentimentType,
} from '../../services/recommendations_api'
import type {
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'

/**
 * Generate a unique key for an action.
 *
 * Prefers signal_fingerprint when available (globally unique per signal).
 * Falls back to deal_id-signal_type-action_type composite key.
 */
const getActionKey = (
  dealId: number | null,
  signalType: SignalType,
  actionType: ActionTypeEnum,
  signalFingerprint?: string | null
): string =>
  signalFingerprint
    ? signalFingerprint
    : `${dealId ?? 'no-deal'}-${signalType}-${actionType}`

interface UseActionClickManagerParams {
  /** Current summary data */
  summary: ActionSummaryData | null | undefined
}

interface UseActionClickManagerResult {
  /** Handler for when an action is clicked */
  handleActionClick: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum
  ) => Promise<void>
  /** Handler for when a sentiment button is clicked */
  handleSentimentClick: (
    action: EnrichedAction,
    signalType: SignalType,
    actionType: ActionTypeEnum,
    sentiment: SentimentType | null
  ) => Promise<void>
  /** Handler for when a status is changed */
  handleStatusClick: (
    action: EnrichedAction,
    newStatus: ActionClickUpdateStatus
  ) => Promise<void>
  /** Get the existing sentiment for an action */
  getSentimentForAction: (action: EnrichedAction) => SentimentType | null
  /** Get the existing status for an action */
  getStatusForAction: (action: EnrichedAction) => ActionClickStatus | null
  /** Map of action keys to their current status */
  statusMap: Map<string, ActionClickStatus>
}

/**
 * Hook for managing action click tracking, sentiment, and status updates.
 *
 * Handles:
 * - Creating click records on first interaction
 * - Tracking click IDs by action key
 * - Sentiment updates
 * - Status updates
 */
export function useActionClickManager({
  summary,
}: UseActionClickManagerParams): UseActionClickManagerResult {
  // Derive initial maps from existing user_clicks
  const {
    sentimentByAction,
    statusMap: initialStatusMap,
    initialClickIdMap,
  } = useMemo(() => {
    const sentimentMap = new Map<string, SentimentType>()
    const statusMapInit = new Map<string, ActionClickStatus>()
    const idMap = new Map<string, number>()
    if (summary?.user_clicks) {
      for (const click of summary.user_clicks) {
        const key = getActionKey(
          click.deal_id,
          click.signal_type,
          click.action_type,
          click.signal_fingerprint
        )
        idMap.set(key, click.click_id)
        if (click.sentiment) {
          sentimentMap.set(key, click.sentiment)
        }
        if (click.status && click.status !== 'open') {
          statusMapInit.set(key, click.status)
        }
      }
    }
    return {
      sentimentByAction: sentimentMap,
      statusMap: statusMapInit,
      initialClickIdMap: idMap,
    }
  }, [summary?.user_clicks])

  // Track click IDs by action key for sentiment/status updates.
  // Seeded from server data, then updated as the user creates new clicks.
  const [, setClickIdMap] = useState<Map<string, number>>(new Map())
  const [sentimentMap, setSentimentMap] = useState<
    Map<string, SentimentType | null>
  >(new Map())
  const [statusMap, setStatusMap] = useState<Map<string, ActionClickStatus>>(
    new Map()
  )
  const clickIdMapRef = useRef<Map<string, number>>(new Map())
  const pendingClickCreationRef = useRef<Map<string, Promise<number | null>>>(
    new Map()
  )

  useEffect(() => {
    setClickIdMap(initialClickIdMap)
    setSentimentMap(new Map(sentimentByAction))
    setStatusMap(initialStatusMap)
    clickIdMapRef.current = initialClickIdMap
  }, [initialClickIdMap, initialStatusMap, sentimentByAction])

  const setClickIdForKey = useCallback((key: string, clickId: number) => {
    setClickIdMap((prev) => {
      const next = new Map(prev)
      next.set(key, clickId)
      clickIdMapRef.current = next
      return next
    })
  }, [])

  const ensureClickIdForAction = useCallback(
    async (action: EnrichedAction): Promise<number | null> => {
      if (!summary || !summary.id) {
        return null
      }
      const summaryId = summary.id

      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )
      const existingClickId = clickIdMapRef.current.get(key)

      if (existingClickId) {
        return existingClickId
      }

      const existingPending = pendingClickCreationRef.current.get(key)
      if (existingPending) {
        return existingPending
      }

      const pendingPromise = (async () => {
        try {
          const response = await recordActionClick({
            action_summary_id: summaryId,
            signal_type: action.signal_type,
            action_type: action.action_type,
            deal_id: action.deal_id,
            contact_company_id: action.contact_company_id,
            action_text: action.action,
            recommendation_text: action.recommendation,
            signal_fingerprint: action.signal_fingerprint,
          })

          if (response?.click_id) {
            setClickIdForKey(key, response.click_id)
            return response.click_id
          }

          return null
        } catch (err) {
          console.error('Failed to record action click:', err)
          return null
        } finally {
          pendingClickCreationRef.current.delete(key)
        }
      })()

      pendingClickCreationRef.current.set(key, pendingPromise)
      return pendingPromise
    },
    [setClickIdForKey, summary]
  )

  // Helper to get existing sentiment for an action (reads from local state)
  const getSentimentForAction = useCallback(
    (action: EnrichedAction): SentimentType | null => {
      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )
      return sentimentMap.get(key) ?? null
    },
    [sentimentMap]
  )

  // Helper to get existing status for an action
  const getStatusForAction = useCallback(
    (action: EnrichedAction): ActionClickStatus | null => {
      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )
      return statusMap.get(key) ?? null
    },
    [statusMap]
  )

  const handleActionClick = useCallback(
    async (
      action: EnrichedAction,
      signalType: SignalType,
      actionType: ActionTypeEnum
    ) => {
      if (!summary || !summary.id) return

      try {
        const clickId = await ensureClickIdForAction({
          ...action,
          signal_type: signalType,
          action_type: actionType,
        })

        if (!clickId) {
          console.error('Failed to resolve click ID for action tracking')
        }
      } catch (err) {
        console.error('Failed to record action click:', err)
      }
    },
    [ensureClickIdForAction, summary]
  )

  const handleSentimentClick = useCallback(
    async (
      action: EnrichedAction,
      signalType: SignalType,
      actionType: ActionTypeEnum,
      sentiment: SentimentType | null
    ) => {
      if (!summary || !summary.id) return

      // Optimistically update local state so the UI reflects immediately
      const key = getActionKey(
        action.deal_id,
        signalType,
        actionType,
        action.signal_fingerprint
      )
      setSentimentMap((prev) => {
        const next = new Map(prev)
        if (sentiment === null) {
          next.delete(key)
        } else {
          next.set(key, sentiment)
        }
        return next
      })
      if (sentiment === 'negative') {
        setStatusMap((prev) => new Map(prev).set(key, 'dismissed'))
      }

      try {
        const existingClickId = await ensureClickIdForAction({
          ...action,
          signal_type: signalType,
          action_type: actionType,
        })

        if (existingClickId) {
          await updateActionClickSentiment({
            click_id: existingClickId,
            sentiment,
          })

          // Thumbs down auto-dismisses the action (status already set optimistically)
          if (sentiment === 'negative') {
            await updateActionClickStatus({
              click_id: existingClickId,
              status: 'dismissed',
            })
          }
        } else {
          console.error('Cannot update sentiment without a click ID')
        }
      } catch (err) {
        console.error('Failed to record sentiment:', err)
      }
    },
    [ensureClickIdForAction, summary]
  )

  const handleStatusClick = useCallback(
    async (action: EnrichedAction, newStatus: ActionClickUpdateStatus) => {
      if (!summary || !summary.id) return

      const key = getActionKey(
        action.deal_id,
        action.signal_type,
        action.action_type,
        action.signal_fingerprint
      )

      try {
        const existingClickId = await ensureClickIdForAction(action)

        if (existingClickId) {
          await updateActionClickStatus({
            click_id: existingClickId,
            status: newStatus,
          })
        } else {
          console.error('Cannot update status without a click ID')
          return
        }

        setStatusMap((prev) => new Map(prev).set(key, newStatus))
      } catch (error) {
        console.error('Failed to update action status:', error)
      }
    },
    [ensureClickIdForAction, summary]
  )

  return {
    handleActionClick,
    handleSentimentClick,
    handleStatusClick,
    getSentimentForAction,
    getStatusForAction,
    statusMap,
  }
}

// Export getActionKey for use in other components if needed
export { getActionKey }
